import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { callUIAPI } from '../../../utils/apiUtils';
import * as apiConstants from '../../../utils/apiUtilsConstants';
import DataListView from '../../../components/listScreen/DataListView';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import {
  DEFAULT_PAGE_SIZE,
  SORT_DIRECTION_ASCENDNG,
  DATATYPE_DATETIME,
} from '../../../components/dataTable/constants';
import './Firm.css';

const COLUMNS = [
  { key: 'firstName', label: 'First Name', value: 'firstName', sortable: true },
  { key: 'lastName',  label: 'Last Name',  value: 'lastName',  sortable: true },
  { key: 'email',     label: 'Email',       value: 'email',     sortable: true },
  {
    key: 'isActive', label: 'Status', value: 'isActive', sortable: true,
    formatter: (val) => (val ? 'Active' : 'Inactive'),
  },
  {
    key: 'lastSuccessfulLogin', label: 'Last Login', value: 'lastSuccessfulLogin',
    sortable: true, dataType: DATATYPE_DATETIME, format: 'LL/dd/yyyy hh:mm a',
  },
  {
    key: 'lockoutUntil', label: 'Locked', value: 'lockoutUntil', sortable: false,
    formatter: (val) => (val && new Date(val) > new Date() ? 'Locked' : ''),
  },
];

const FirmUsers = ({ firmId }) => {
  const navigate = useNavigate();

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [orderBy, setOrderBy] = useState('firstName');
  const [sortDirection, setSortDirection] = useState(SORT_DIRECTION_ASCENDNG);
  const [searchQuery, setSearchQuery] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const isDeleteEnabled = deleteConfirmText.trim().toLowerCase() === 'delete';

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const body = { pageNumber: page, pageSize, orderBy, sortDirection };

      if (appliedSearch.trim()) {
        body.group = {
          operator: 'OR',
          criteria: [
            { fieldName: 'firstName', operator: 'contains', value: appliedSearch.trim() },
            { fieldName: 'lastName',  operator: 'contains', value: appliedSearch.trim() },
            { fieldName: 'email',     operator: 'contains', value: appliedSearch.trim() },
          ],
        };
      }

      const res = await callUIAPI(
        `bo/firms/${firmId}/users/search`, body, apiConstants.METHOD_POST, true,
      );
      setData(res.data.list || []);
      setTotalRecords(res.data.totalCount || 0);
    } catch (err) {
      showErrorMessage(err?.response?.data?.message || 'Failed to load users.');
      setData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [firmId, page, pageSize, orderBy, sortDirection, appliedSearch]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSort = (field, newDirection) => {
    setOrderBy(field);
    setSortDirection(newDirection);
    setPage(1);
  };

  const handlePageChange = (newPage) => setPage(newPage);

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setPage(1);
  };

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
    setPage(1);
  };

  const handleSearchQueryChange = (value) => setSearchQuery(value);

  const getUserDisplayName = (row) =>
    (row.firstName + ' ' + (row.lastName || '')).trim();

  const handleUnlock = async (event, row) => {
    try {
      await callUIAPI(
        `bo/firms/${firmId}/users/unlock/${row.id}`, null, apiConstants.METHOD_PUT, true,
      );
      showSuccessMessage(`User '${getUserDisplayName(row)}' unlocked.`);
      return { ...row, lockoutUntil: null };
    } catch (err) {
      showErrorMessage(err?.response?.data?.message || 'Failed to unlock user.');
      throw err;
    }
  };

  const handleRevokeSessions = async (event, row) => {
    if (!window.confirm(
      `Revoke all active sessions for "${getUserDisplayName(row)}"?\n\nThey will be logged out of all devices and browsers immediately and will need to sign in again.`
    )) {
      throw new Error('cancelled');
    }
    try {
      await callUIAPI(
        `bo/firms/${firmId}/users/${row.id}/revoke-sessions`, null, apiConstants.METHOD_PUT, true,
      );
      showSuccessMessage(`All sessions revoked for '${getUserDisplayName(row)}'.`);
    } catch (err) {
      if (err.message === 'cancelled') throw err;
      showErrorMessage(err?.response?.data?.message || 'Failed to revoke sessions.');
      throw err;
    }
  };

  const openDeleteDialog = (event, row) => {
    event.stopPropagation();
    setDeleteTarget(row);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const closeDeleteDialog = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteConfirmText('');
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget || !isDeleteEnabled) return;
    try {
      setDeleting(true);
      setDeleteError(null);
      await callUIAPI(
        `bo/firms/${firmId}/users/${deleteTarget.id}`, null, apiConstants.METHOD_DELETE, true,
      );
      showSuccessMessage(`User '${getUserDisplayName(deleteTarget)}' deleted successfully.`);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      fetchUsers();
    } catch (err) {
      setDeleteError(err?.response?.data?.message || 'Failed to delete user.');
    } finally {
      setDeleting(false);
    }
  };

  const handleUpdateRow = (updatedRow) => {
    setData((prev) => prev.map((r) => (r.id === updatedRow.id ? { ...r, ...updatedRow } : r)));
  };

  const rowActions = [
    {
      class: 'bx bx-lock-open-alt',
      tooltipText: 'Unlock account',
      handler: handleUnlock,
      displayCriteriaFunction: (row) => row.lockoutUntil && new Date(row.lockoutUntil) >= new Date(),
    },
    {
      class: 'bx bx-log-out',
      tooltipText: 'Revoke all sessions',
      handler: handleRevokeSessions,
    },
    {
      class: 'bx bx-trash',
      tooltipText: 'Delete user',
      handler: openDeleteDialog,
    },
  ];

  const handleRowClick = (row) => {
    navigate(`/bo/firms/${firmId}/users/${row.id}`);
  };

  const deleteTargetName = deleteTarget ? getUserDisplayName(deleteTarget) : '';

  return (
    <>
      <DataListView
        entityName={`boFirmUsers_${firmId}`}
        heading=""
        data={data}
        displayColumns={COLUMNS}
        customColumns={COLUMNS}
        totalRecords={totalRecords}
        currentPage={page}
        pageSize={pageSize}
        orderBy={orderBy}
        sortDirection={sortDirection}
        isLoading={loading}
        showSearch={true}
        showAddButton={false}
        searchQuery={searchQuery}
        onSearchQueryChange={handleSearchQueryChange}
        onSearch={handleSearch}
        onSort={handleSort}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onDelete={() => {}}
        onUpdateRow={handleUpdateRow}
        rowActions={rowActions}
        enableRowClick={true}
        onRowClick={handleRowClick}
      />

      {}
      {deleteTarget && (
        <div className="firm-overlay" onClick={closeDeleteDialog}>
          <div className="firm-dialog" onClick={(e) => e.stopPropagation()}>
            {deleting ? (
              <div className="firm-deleting-container">
                <div className="firm-spinner" />
                <p className="firm-deleting-text">Deleting user…</p>
                <p className="firm-deleting-subtext">This may take a moment.</p>
              </div>
            ) : (
              <>
                <div className="firm-dialog-icon">⚠</div>
                <h3 className="firm-dialog-title">Delete &ldquo;{deleteTargetName}&rdquo;?</h3>
                <p className="firm-dialog-body">
                  This will <strong>permanently delete</strong> this user from the
                  firm. They will lose all access and any data associated with their
                  account. This action cannot be reversed.
                </p>

                {deleteError && (
                  <p className="firm-dialog-error">{deleteError}</p>
                )}

                <label className="firm-confirm-label">
                  Type <strong>delete</strong> to confirm
                </label>
                <input
                  className="firm-confirm-input"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="delete"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && isDeleteEnabled) confirmDelete();
                    if (e.key === 'Escape') closeDeleteDialog();
                  }}
                />

                <div className="firm-dialog-actions">
                  <button
                    className="firm-btn firm-btn-secondary"
                    onClick={closeDeleteDialog}
                  >
                    Cancel
                  </button>
                  <button
                    className="firm-btn-confirm-delete"
                    disabled={!isDeleteEnabled}
                    onClick={confirmDelete}
                  >
                    Delete user permanently
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FirmUsers;